import { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { ApolloError } from '@apollo/client/errors';
import { useHistory, useParams } from 'react-router-dom';
import { ToastType } from '@alefeducation/ui-elements-library-core';
import { UserType } from './AddMember';
import { isEmailValid, isPasswordStrong } from '../../utils/Validators';
import {
  CREATE_USER_V3,
  CreateUserInputResponseV3,
  CreateUserInputV3,
  MembershipInputV3,
} from '../../apollo/queries/v3/createUserV3.queries';
import useMemberForm from '../hooks/useMemberForm';
import { GET_USER_BY_EMAIL_V3 } from '../../apollo/queries/v3/getUserByEmailV3.queries';
import { Member, MembershipV2 } from '../../apollo/queries';
import {
  ADD_MEMBERSHIP_V2,
  AddMembershipInputV2,
  AddMembershipResponseDataV2,
} from '../../apollo/queries/v3/addMember.queries';
import useAddEditUserRole from '../hooks/useAddEditUserRole';
import isInCCLiframe from '../../utils/isInCCLiframe';
import { injectAutoHideToastOfType } from '../../utils/Toast';
import {
  MEMBER_SUCCESSFULLY_ADDED,
  SOMETHING_WENT_WRONG_MESSAGE,
} from '../../utils/constants';
import { FEATURE_TOGGLE } from '../../utils/FeatureToggle';
import useIframeAuth from '../hooks/useIframeAuth';

interface LocalState {
  pageNumber: number;
}

interface userByEmailV3 {
  userByEmailV3: Member;
}

interface Props {
  isOrgMember: boolean;
}

export const useAddMember = ({ isOrgMember = false }: Props) => {
  const history = useHistory();
  const { code: organisationCode } = useParams<{ code: string }>();
  const [email, setEmail] = useState<string>('');
  const [emailValid, setEmailValid] = useState<boolean>(true);
  const {
    name,
    firstName,
    lastName,
    password,
    confirmPassword,
    setName,
    setFirstName,
    setLastName,
    setPassword,
    setConfirmPassword,
  } = useMemberForm();
  const { selectedRoles, setSelectedRoles, getMembershipInputV3 } =
    useAddEditUserRole();
  const [showSuccessToast, setShowSuccessToast] = useState<boolean>(false);
  const [isCheckClicked, setIsCheckClicked] = useState<boolean>(false);
  const [userType, setUserType] = useState<UserType | undefined>(undefined);
  const [userId, setUserId] = useState<string>('');
  const [userData, setUserData] = useState<Member | null>(null);
  const [fetchUserError, setFetchUserError] = useState<ApolloError | boolean>(
    false,
  );
  const { isAuthorized } = useIframeAuth();
  const hasUpdatePermission = isAuthorized('update:user');
  const hasAssignPermission = isAuthorized('assign:user');
  const hasCreatePermission = isAuthorized('create:user');

  const state = history.location.state as LocalState;
  const pageNumber = state ? state.pageNumber : 0;

  const validateEmail = () => {
    setEmailValid(isEmailValid(email));
  };

  const findMembership = (membership: MembershipV2[]) =>
    membership.find(
      (membership) => membership.organisation === organisationCode,
    );

  const userHasMemberships = (user: Member) => {
    if (user.membershipsV2.length > 0) {
      if (isOrgMember) {
        return findMembership(user.membershipsV2);
      }
      return user.membershipsV2.length > 0;
    }
  };

  const [getUser, { loading }] = useLazyQuery<userByEmailV3 | null>(
    GET_USER_BY_EMAIL_V3,
    {
      onCompleted: (data: userByEmailV3 | null) => {
        const userByEmailV3 = data?.userByEmailV3;
        const isNextGenUser = userByEmailV3 && userByEmailV3.uuid;
        if (userByEmailV3 && userByEmailV3.id) {
          if (userHasMemberships(userByEmailV3)) {
            setUserType(UserType.ORG_MEMBER);
            setUserId(userByEmailV3.id);
            setUserData(null);
          } else {
            setUserType(UserType.EXISTING);
            setUserData(userByEmailV3);
            setName(userByEmailV3.name || '');
            setFirstName(userByEmailV3?.firstName || '');
            setLastName(userByEmailV3?.lastName || '');
          }
        } else if (isNextGenUser) {
          setUserType(UserType.ALEF_ONLY);
          setFirstName(userByEmailV3?.firstName || '');
          setLastName(userByEmailV3?.lastName || '');
        } else if (userByEmailV3 && userByEmailV3.email) {
          setUserType(UserType.EXISTING);
          setUserData(userByEmailV3);
        } else {
          setUserType(UserType.NEW);
          setName('');
          setUserData(null);
        }
      },
      onError: setFetchUserError,
    },
  );

  const handleOnComplete = () => {
    setShowSuccessToast(true);
    if (isInCCLiframe) {
      redirectToOrgMembersPage();
      injectAutoHideToastOfType(
        'toast-member-list',
        MEMBER_SUCCESSFULLY_ADDED,
        ToastType.SUCCESS,
      );
    } else {
      history.go(0);
    }
  };

  const onError = (error: ApolloError) => {
    injectAutoHideToastOfType(
      'add-member-error-toast',
      SOMETHING_WENT_WRONG_MESSAGE,
      ToastType.ERROR,
    );
  };

  const [createUserV3] = useMutation(CREATE_USER_V3, {
    onCompleted: (data: CreateUserInputResponseV3) => {
      if (data.createUserV3) {
        handleOnComplete();
      }
    },
    onError,
  });

  const [addMembershipV2] = useMutation(ADD_MEMBERSHIP_V2, {
    onCompleted: (data: AddMembershipResponseDataV2) => {
      if (data.addMembershipV2) {
        handleOnComplete();
      }
    },
    onError,
  });

  function redirectToOrgMembersPage() {
    history.push(`/organisations/${organisationCode}/members`, {
      pageNumber,
    });
  }

  function redirectToMembersPage() {
    history.push(`/members`);
  }

  const onCancelClick = () => {
    if (organisationCode) {
      redirectToOrgMembersPage();
    } else {
      redirectToMembersPage();
    }
  };

  const basicInfo = () => ({
    name,
    firstName,
    lastName,
    email,
    password,
  });

  const handleCreateUserV3 = async () => {
    const memberships: MembershipInputV3[] = getMembershipInputV3();
    const input: CreateUserInputV3 = {
      ...basicInfo(),
      memberships,
    };

    await createUserV3({ variables: { input } });
  };

  const handleAddMembershipV2 = async () => {
    const memberships = getMembershipInputV3();

    const input: AddMembershipInputV2 = {
      id: userData!.id,
      memberships,
    };

    await addMembershipV2({ variables: { input } });
  };

  const onAddMember = async () => {
    if (userType === UserType.NEW || userType === UserType.ALEF_ONLY) {
      await handleCreateUserV3();
    } else {
      await handleAddMembershipV2();
    }
  };

  const passwordMismatch = () => password !== confirmPassword;

  const getIsDisabled = () => {
    if (isInCCLiframe) {
      switch (userType) {
        case UserType.ALEF_ONLY: {
          return !hasCreatePermission;
        }
        case UserType.NEW: {
          return !hasCreatePermission;
        }
        case UserType.EXISTING: {
          return !hasAssignPermission;
        }
        case UserType.ORG_MEMBER:
        default: {
          return true;
        }
      }
    } else {
      return (
        !userData &&
        userType !== UserType.NEW &&
        userType !== UserType.ALEF_ONLY
      );
    }
  };

  const disabled = getIsDisabled();

  const isEmpty = (value: string) => value === '';

  const areBasicDetailsMatching = () => {
    const areAnyFieldsEmpty =
      isEmpty(password) ||
      isEmpty(confirmPassword) ||
      (!FEATURE_TOGGLE.ENABLE_AUTH0_INTEGRATION ? isEmpty(name) : false) ||
      (FEATURE_TOGGLE.ENABLE_AUTH0_INTEGRATION ? isEmpty(firstName) : false) ||
      (FEATURE_TOGGLE.ENABLE_AUTH0_INTEGRATION ? isEmpty(lastName) : false);

    const isUserTypeNew = userType === UserType.NEW;
    const isEmailInValid = !isEmailValid(email);
    const passMisMatch = passwordMismatch();
    const isPasswordWeak = !isPasswordStrong(password);

    return (
      (isUserTypeNew && isEmailInValid) ||
      passMisMatch ||
      isPasswordWeak ||
      areAnyFieldsEmpty
    );
  };

  const isFirstNameAndLastNameFilled = () => {
    return !isEmpty(firstName) && !isEmpty(lastName);
  };

  const isPasswordsFilled = () => {
    if (isEmpty(password) && isEmpty(confirmPassword)) {
      return false;
    }
    //If password is filled, only then validate password
    return passwordMismatch() || !isPasswordStrong(password);
  };

  const isFormInValid = (): boolean => {
    if (loading || email === '' || disabled) {
      return true;
    }

    if (userType === UserType.ALEF_ONLY) {
      return !isFirstNameAndLastNameFilled() || isPasswordsFilled();
    }
    const basicDetailNotMatching =
      userType !== UserType.EXISTING && areBasicDetailsMatching();

    if (basicDetailNotMatching) {
      return true;
    }

    return isOrgMember && selectedRoles.length === 0;
  };

  const onCheckEmail = () => {
    if (isEmailValid(email)) {
      getUser({ variables: { email: email.toLowerCase() } });
    }
  };

  const form = {
    isCheckClicked,
    setIsCheckClicked,
    selectedRoles,
    email,
    setEmail,
    emailValid,
    password,
    confirmPassword,
    name,
    firstName,
    lastName,
    isFormInValid,
    validateEmail,
    onAddMember,
    onCheckEmail,
    onRoleChange: setSelectedRoles,
    onNameChange: setName,
    onFirstNameChange: setFirstName,
    onLastNameChange: setLastName,
    onPassChange: setPassword,
    onConfirmPassChange: setConfirmPassword,
    onCancelClick,
    userId,
  };

  const toast = { showSuccessToast, setShowSuccessToast };
  const userQuery = {
    fetchUserError,
    loading,
  };

  return {
    form,
    toast,
    userQuery,
    userType,
    disabled,
    hasUpdatePermission,
    pageNumber,
    organisationCode,
    userData,
  };
};
